import type { DateRange } from 'react-day-picker';
import { defaultValidateDateString, defaultWorkTimeResetDate } from 'src/components/common/common';
/**
 * 날짜 처리 관련 유틸리티 함수들
 */

/** 점검년월 기준 일자 동적 그리드 컬럼 메타 (EQM2070 단일 / EQM2080 2단 헤더 공통) */
export interface MonthDayColumn {
	/** 컬럼 헤더 (예: "1일(월)") */
	headerName: string;
	/** 날짜 키 (YYYY-MM-DD) */
	dateKey: string;
	/** 요일 (일~토) */
	dayOfWeekNm?: string;
	/** 주말 등 헤더 강조용 CSS class */
	headerClass?: string;
	/** 일(day) 2자리 — dayValues/dayMeta 키 (예: "01") */
	dayDd?: string;
	/** 단일 일자 컬럼 ID (예: day_01) */
	colId?: string;
	/** 2단 헤더 - 1차 컬럼 ID (예: day1_01) */
	colId1?: string;
	/** 2단 헤더 - 2차 컬럼 ID (예: day2_01) */
	colId2?: string;
	/** 2단 헤더 - 그룹 컬럼 ID (예: day_group_01) */
	groupColId?: string;
}

export const DAY_OF_WEEK_NAMES_KO = ["일", "월", "화", "수", "목", "금", "토"] as const;

export const MONTH_DAY_HEADER_CLASS = {
	SUNDAY: "month-day-sunday-header",
	SATURDAY: "month-day-saturday-header",
} as const;

/** YYYY-MM-DD(또는 ISO 앞 10자) 날짜 문자열을 잘라서 dateKey 생성 */
export function toDateKeyFromString(dateStr?: string): string | null {
	if (!dateStr) return null;
	const dateKey = dateStr.slice(0, 10);
	if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return null;
	return dateKey;
}

/** YYYY-MM-DD dateKey 기준 요일 인덱스 (0=일, 6=토) */
export function getDayOfWeekFromString(dateKey: string): number {
	const [year, month, day] = dateKey.split("-").map(Number);
	if (!year || !month || !day) return -1;
	return new Date(year, month - 1, day).getDay();
}

/** YYYY-MM-DD(또는 ISO 앞 10자) 날짜 문자열 기준 요일 인덱스 (0=일, 6=토). 유효하지 않으면 null */
export function getDayOfWeekIndexFromDateString(dateStr?: string): number | null {
	const dateKey = toDateKeyFromString(dateStr);
	if (!dateKey) return null;
	const dayOfWeek = getDayOfWeekFromString(dateKey);
	return dayOfWeek < 0 ? null : dayOfWeek;
}

/** YYYY-MM-DD(또는 ISO 앞 10자) 날짜 문자열 기준 요일명 (일~토) */
export function getDayNameFromDateString(dateStr?: string): string {
	const dayOfWeek = getDayOfWeekIndexFromDateString(dateStr);
	if (dayOfWeek === null) return "";
	return DAY_OF_WEEK_NAMES_KO[dayOfWeek] ?? "";
}

/** 일자 컬럼 헤더명 (예: "1일(월)") */
export function formatMonthDayHeaderName(day: number, dateKey: string): string {
	const dayOfWeekNm = getDayNameFromDateString(dateKey);
	return dayOfWeekNm ? `${day}일(${dayOfWeekNm})` : `${day}일`;
}

/** 토/일요일 헤더 강조 CSS class */
export function getMonthDayHeaderClass(dateKey: string): string | undefined {
	const dayOfWeek = getDayOfWeekFromString(dateKey);
	if (dayOfWeek === 0) return MONTH_DAY_HEADER_CLASS.SUNDAY;
	if (dayOfWeek === 6) return MONTH_DAY_HEADER_CLASS.SATURDAY;
	return undefined;
}

/** 헤더 CSS class 병합 */
export function mergeMonthDayHeaderClasses(
	...classes: (string | undefined)[]
): string | undefined {
	const merged = classes.filter(Boolean).join(" ");
	return merged || undefined;
}

/** 점검년월(YYYY-MM) 기준 일자 컬럼 메타 공통 필드 생성 */
export function createMonthDayColumnMeta(day: number, inspectDtYm: string): Pick<
	MonthDayColumn,
	"headerName" | "dateKey" | "dayOfWeekNm" | "headerClass"
> {
	const dayStr = String(day).padStart(2, "0");
	const dateKey = `${inspectDtYm}-${dayStr}`;
	return {
		headerName: formatMonthDayHeaderName(day, dateKey),
		dateKey,
		dayOfWeekNm: getDayNameFromDateString(dateKey),
		headerClass: getMonthDayHeaderClass(dateKey),
	};
}

/** 일자 컬럼 colId 생성 (예: day_01) */
export function formatDayColId(day: number): string {
	return `day_${String(day).padStart(2, "0")}`;
}

/** day_{dd} colId 생성 → 2자리 일 추출 (예: day_01 → "01") */
export function parseDayColId(colId: string): string | null {
	const matched = /^day_(\d{2})$/.exec(colId);
	return matched?.[1] ?? null;
}

/** 2단 헤더 1차/2차 colId 생성 (예: day1_01, day2_15) */
export function formatDayRoundColId(round: 1 | 2, day: number): string {
	return `day${round}_${String(day).padStart(2, "0")}`;
}

/** 2단 헤더 그룹 colId 생성 (예: day_group_01) */
export function formatDayGroupColId(day: number): string {
	return `day_group_${String(day).padStart(2, "0")}`;
}

/** day1_/day2_ colId 생성 → 2자리 일·차수 추출 (예: day1_01 → "01", day2_15 → "15") */
export function parseDayRoundColId(field: string): { dayDd: string; round: 1 | 2 } | null {
	const day1Match = /^day1_(\d{2})$/.exec(field);
	if (day1Match) return { dayDd: day1Match[1], round: 1 };
	const day2Match = /^day2_(\d{2})$/.exec(field);
	if (day2Match) return { dayDd: day2Match[1], round: 2 };
	return null;
}

/** YYYY-MM-DD dateKey에서 2자리 일 추출 (예: YYYY-MM-DD → "01") */
export function extractDayDdFromDateKey(dateKey: string): string {
	return dateKey.slice(-2);
}

/** Filter.inspectDt(YYYY-MM) + dayDd 생성 → YYYY-MM-DD (예: YYYY-MM-DD → "01") */
export function buildDateKey(inspectDtYm: string, dayDd: string): string {
	return `${inspectDtYm}-${dayDd}`;
}

/** MonthDayColumn에서 편집 가능한 leaf colId 목록 추출 (예: [day1_01, day2_15] → ["day1_01", "day2_15"]) */
export function getEditableDayColIds(columns: MonthDayColumn[]): string[] {
	return columns.flatMap((column) => {
		if (column.colId1 && column.colId2) {
			return [column.colId1, column.colId2];
		}
		if (column.colId) {
			return [column.colId];
		}
		return [];
	});
}

/**
 * 로컬 시간 기준으로 YYYY-MM-DD 형식 날짜 문자열 생성
 * @param date Date 객체
 * @returns YYYY-MM-DD 형식의 문자열
 */
export const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 현지 날짜의 연월을 YYYYMM 형식으로 만든다.
 * @param date 연월을 추출할 현지 날짜
 * @returns YYYYMM 형식의 연월
 */
export const formatLocalYearMonth = (date: Date): string =>
  `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`;

/**
 * YYYYMM 문자열을 YYYY-MM 형식으로 바꾼다.
 * @param yearMonth YYYYMM 형식의 연월
 * @returns 여섯 자리이면 YYYY-MM, 아니면 입력 문자열
 */
export const formatYearMonthWithHyphen = (yearMonth: string): string =>
  yearMonth.length === 6 ? `${yearMonth.slice(0, 4)}-${yearMonth.slice(4)}` : yearMonth;

/**
 * YYYYMM 형식을 "YYYY년 M월" 형식으로 변환
 * @param yearMonth YYYYMM 형식의 문자열
 * @returns "YYYY년 M월" 형식의 문자열
 */
export const formatYearMonth = (yearMonth: string): string => {
  if (!yearMonth || yearMonth.length !== 6) {
    return yearMonth || '미분류';
  }
  
  const year = yearMonth.substring(0, 4);
  const month = parseInt(yearMonth.substring(4, 6));
  return `${year}년 ${month}월`;
};

/**
 * 날짜 범위 문자열 생성
 * @param startDate 시작 날짜
 * @param endDate 종료 날짜
 * @returns "YYYY.MM.DD ~ YYYY.MM.DD" 형식의 문자열
 */
export const formatDateRange = (startDate: Date, endDate: Date): string => {
  return `${startDate.toLocaleDateString('ko-KR')} ~ ${endDate.toLocaleDateString('ko-KR')}`;
};

/**
 * 오늘부터 지정된 일수만큼 이전 날짜 범위 계산
 * @param days 이전 일수
 * @returns { fromDate, toDate }
 */
export const calculateDateRange = (days: number): { fromDate: Date; toDate: Date } => {
  const toDate = new Date();
  const fromDate = new Date();
  fromDate.setDate(toDate.getDate() - days + 1);
  
  return { fromDate, toDate };
};

/**
 * 이번 달 첫날과 마지막날 계산
 * @returns { fromDate, toDate }
 */
export const getCurrentMonthRange = (): { fromDate: Date; toDate: Date } => {
  const now = new Date();
  const fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
  const toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  return { fromDate, toDate };
};

/**
 * 지난달 첫날과 마지막날 계산
 * @returns { fromDate, toDate }
 */
export const getLastMonthRange = (): { fromDate: Date; toDate: Date } => {
  const now = new Date();
  const fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const toDate = new Date(now.getFullYear(), now.getMonth(), 0);
  
  return { fromDate, toDate };
};

/**
 * Date 기준 해당 월의 일수 반환
 */
export const getDaysInMonthByDate = (date: Date): number => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

/**
 * 점검년월(YYYY-MM) 문자열 기준 해당 월의 일수 반환
 */
export const getDaysInMonth = (inspectDtYm: string): number => {
  const [year, month] = inspectDtYm.split("-").map(Number);
  if (!year || !month) return 0;
  return new Date(year, month, 0).getDate();
};

/**
 * 월의 날짜 범위(DateRange)
 */
export const getMonthDateRange = (date: Date = new Date()): DateRange => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const lastDay = getDaysInMonthByDate(date);
  return {
    from: new Date(year, month, 1),
    to: new Date(year, month, lastDay),
  };
};

/**
 * 현재 월의 날짜 범위(DateRange)
 */
export const getCurrentMonthDateRange = (): DateRange => {
  return getMonthDateRange(new Date());
};

/**
 * 이전 월의 날짜 범위(DateRange)
 */
export const getPreviousMonthDateRange = (): DateRange => {
  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return getMonthDateRange(prevMonth);
};

/**
 * 다음 월의 날짜 범위(DateRange)
 */
export const getNextMonthDateRange = (): DateRange => {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return getMonthDateRange(nextMonth);
};

/**
 * 특정 연/월의 날짜 범위(DateRange)
 */
export const getSpecificMonthDateRange = (year: number, month: number): DateRange => {
  const date = new Date(year, month, 1);
  return getMonthDateRange(date);
};

/**
 * DateRange 유효성 검사
 */
export const isValidDateRange = (dateRange: DateRange | undefined): boolean => {
  if (!dateRange || !dateRange.from || !dateRange.to) {
    return false;
  }
  return dateRange.from <= dateRange.to;
};

/**
 * DateRange의 일수 계산(포함)
 */
export const getDateRangeDays = (dateRange: DateRange | undefined): number => {
  if (!dateRange || !dateRange.from || !dateRange.to) {
    return 0;
  }
  const from = dateRange.from;
  const to = dateRange.to;
  const timeDiff = to.getTime() - from.getTime();
  const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  return dayDiff + 1;
};

/**
 * ISO(YYYY-MM-DD) 문자열 변환
 */
export const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 작업시간 등에서 쓰는 미등록(센티널) 일시인지 판별.
 * 1900-01-01(로컬 / UTC / +09:00 변형) 또는 빈·무효 값이면 true.
 */
export const isUnsetWorkDateTime = (time: Date | string | null | undefined): boolean => {
  if (time === undefined || time === null || time === '') {
    return true;
  }

  const d = new Date(time);
  if (isNaN(d.getTime())) {
    return true;
  }

  const targetTime = d.getTime();
  const defaultLocalTime = new Date(defaultValidateDateString).getTime();

  if (targetTime === defaultLocalTime || targetTime === defaultWorkTimeResetDate) {
    return true;
  }

  const isLocalReset = d.getFullYear() === 1900 && d.getMonth() === 0 && d.getDate() === 1;
  const isUtcReset = d.getUTCFullYear() === 1900 && d.getUTCMonth() === 0 && d.getUTCDate() === 1;
  return isLocalReset || isUtcReset;
}; 



const getFutureWeekdayFrom = (daysFromNow: number): Date => {
  const today = new Date();
  const checkDate = new Date(today.getTime());

  // 지정된 일수만큼 뒤로 이동하여 확인 시작
  checkDate.setDate(checkDate.getDate() + daysFromNow);

  while (true) {
    const dayOfWeek = checkDate.getDay();
    // 주말(토요일: 6, 일요일: 0)이 아니면 해당 날짜를 반환합니다.
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      return checkDate;
    }
    // 주말이면 하루를 더해 다음 날을 확인합니다.
    checkDate.setDate(checkDate.getDate() + 1);
  }
};

/**
 * 당일제외 가장 미래의 평일 반환 - 주말 제외
 */
export const getFutureWeekday = (): Date => {
  return getFutureWeekdayFrom(1); // 내일부터 시작
};

/**
 * 당일과 명일 제외 가장 미래의 평일 반환 - 주말 제외
 */
export const getFutureWeekdayExceptTodayAndTomorrow = (): Date => {
  return getFutureWeekdayFrom(2); // 모레부터 시작
};